import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy, Medal, TrendingUp, Hash, Flame } from "lucide-react";

export default function Leaderboard() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/leaderboard?period=${period}`, { credentials: "include" });
        if (res.ok) setEntries(await res.json());
      } catch (error) {
        toast.error("Failed to load leaderboard");
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [period]);

  const getRankStyle = (rank) => {
    if (rank === 1) return { bg: "bg-yellow-500/10", border: "border-yellow-500/50", text: "text-yellow-500" };
    if (rank === 2) return { bg: "bg-slate-400/10", border: "border-slate-400/50", text: "text-slate-400" };
    if (rank === 3) return { bg: "bg-amber-700/10", border: "border-amber-700/50", text: "text-amber-700" };
    return { bg: "bg-muted/50", border: "border-transparent", text: "text-muted-foreground" };
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-700" />;
    return <span className="font-mono text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Leaderboard</h1>
          <p className="text-muted-foreground mt-1">See how you rank against your team</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Top 3 Podium */}
      {entries.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 items-end" data-testid="podium">
          {/* 2nd Place */}
          <Card className={`border ${getRankStyle(2).border} ${getRankStyle(2).bg}`}>
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-400/20 mx-auto mb-2 flex items-center justify-center overflow-hidden">
                {entries[1].picture ? (
                  <img src={entries[1].picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <Medal className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <p className="font-medium text-sm truncate">{entries[1].name}</p>
              <p className={`font-heading text-3xl font-bold ${getRankStyle(2).text}`}>{entries[1].avg_score}</p>
              <p className="text-xs text-muted-foreground">avg score</p>
              <Badge variant="outline" className="mt-2 text-xs">{entries[1].session_count} sessions</Badge>
            </CardContent>
          </Card>

          {/* 1st Place */}
          <Card className={`border-2 ${getRankStyle(1).border} ${getRankStyle(1).bg} -mt-4`}>
            <CardContent className="p-5 text-center">
              <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-1" />
              <div className="w-14 h-14 rounded-full bg-yellow-500/20 mx-auto mb-2 flex items-center justify-center overflow-hidden">
                {entries[0].picture ? (
                  <img src={entries[0].picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <Flame className="w-7 h-7 text-yellow-500" />
                )}
              </div>
              <p className="font-semibold truncate">{entries[0].name}</p>
              <p className={`font-heading text-4xl font-bold ${getRankStyle(1).text}`}>{entries[0].avg_score}</p>
              <p className="text-xs text-muted-foreground">avg score</p>
              <Badge variant="outline" className="mt-2 text-xs">{entries[0].session_count} sessions</Badge>
            </CardContent>
          </Card>

          {/* 3rd Place */}
          <Card className={`border ${getRankStyle(3).border} ${getRankStyle(3).bg}`}>
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-700/20 mx-auto mb-2 flex items-center justify-center overflow-hidden">
                {entries[2].picture ? (
                  <img src={entries[2].picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <Medal className="w-6 h-6 text-amber-700" />
                )}
              </div>
              <p className="font-medium text-sm truncate">{entries[2].name}</p>
              <p className={`font-heading text-3xl font-bold ${getRankStyle(3).text}`}>{entries[2].avg_score}</p>
              <p className="text-xs text-muted-foreground">avg score</p>
              <Badge variant="outline" className="mt-2 text-xs">{entries[2].session_count} sessions</Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Full Rankings */}
      {entries.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Rankings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {entries.map((entry) => {
                const style = getRankStyle(entry.rank);
                const isMe = entry.user_id === user?.user_id;
                return (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-4 px-6 py-3 ${isMe ? "bg-primary/5" : "hover:bg-muted/30"} transition-colors`}
                    data-testid={`rank-${entry.rank}`}
                  >
                    <div className="w-10 flex-shrink-0 flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {entry.picture ? (
                        <img src={entry.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="text-xs font-bold">{entry.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate flex items-center gap-2 ${isMe ? "text-primary" : ""}`}>
                        <span className="truncate">{entry.name}</span>
                        {isMe && <Badge className="text-[10px] px-1.5 py-0 flex-shrink-0" variant="secondary">You</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{entry.session_count} sessions</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-mono text-xl font-bold ${
                        entry.avg_score >= 80 ? "text-green-500" :
                        entry.avg_score >= 60 ? "text-yellow-500" : "text-red-500"
                      }`}>{entry.avg_score}</p>
                      <p className="text-xs text-muted-foreground">avg</p>
                    </div>
                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <p className="font-mono text-lg font-bold text-primary">{entry.best_score}</p>
                      <p className="text-xs text-muted-foreground">best</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="p-12 text-center">
          <Trophy className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold mb-2">No rankings yet</h3>
          <p className="text-muted-foreground">
            Complete roleplay sessions to appear on the leaderboard
          </p>
        </Card>
      )}
    </div>
  );
}
